/**
 * Screen 6 — Renewals. The screen the front desk lives in.
 *
 * Three sections, each sorted soonest-first, each row one tap from renewing
 * and one tap from copying the number to call. Someone drops off the list the
 * moment they renew — only the latest term per member-and-class is considered,
 * so the list is always work still to do, never a log of work already done.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  countRowsByClass,
  filterSubscriptionRows,
  filterSubscriptionRowsByClass,
  loadRenewalBuckets,
  RECENTLY_EXPIRED_DAYS,
  type SubscriptionRow,
} from '../db/queries';
import { BalanceDueBadge, CopyPhoneButton } from '../components/StatusBadge';
import ClassFilter from '../components/ClassFilter';
import { classLabel } from '@shared/catalog';
import { formatDisplayDate, todayISO } from '@shared/dates';
import { formatPhone } from '@shared/identity';
import { describeRemaining } from '@shared/pricing';

export default function Renewals() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  // In the URL, like the members list, so a filtered view can be bookmarked
  // and survives a refresh mid-shift.
  const classKey = params.get('class') || 'all';

  const buckets = useLiveQuery(() => loadRenewalBuckets(todayISO()), [], undefined);

  const filtered = useMemo(() => {
    if (!buckets) return null;
    const narrow = (rows: SubscriptionRow[]) =>
      filterSubscriptionRows(filterSubscriptionRowsByClass(rows, classKey), query);
    return {
      expiringSoon: narrow(buckets.expiringSoon),
      recentlyExpired: narrow(buckets.recentlyExpired),
      longExpired: narrow(buckets.longExpired),
    };
  }, [buckets, query, classKey]);

  /**
   * Counts span all three buckets, so a chip says how many people in that
   * class need chasing at all — not just how many are in the section you
   * happen to have open.
   */
  const classCounts = useMemo(() => {
    if (!buckets) return {};
    return countRowsByClass([
      ...buckets.expiringSoon,
      ...buckets.recentlyExpired,
      ...buckets.longExpired,
    ]);
  }, [buckets]);

  if (!filtered) return <p className="py-20 text-center text-white/30">Loading…</p>;

  const total =
    filtered.expiringSoon.length + filtered.recentlyExpired.length + filtered.longExpired.length;

  function setClass(key: string) {
    const next = new URLSearchParams(params);
    if (key === 'all') next.delete('class');
    else next.set('class', key);
    setParams(next);
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-6">
      <h1 className="mb-1 text-4xl text-brand">Renewals</h1>
      <p className="mb-5 text-white/40">
        {total === 0
          ? classKey === 'all'
            ? 'Nobody needs chasing right now.'
            : `Nobody in ${classLabel(classKey)} needs chasing.`
          : `${total} to follow up${classKey === 'all' ? '' : ` in ${classLabel(classKey)}`}.`}
      </p>

      <input
        className="field mb-4"
        placeholder="Filter by name, phone or class…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        dir="auto"
      />

      <ClassFilter value={classKey} onChange={setClass} counts={classCounts} className="mb-6" />

      <Bucket
        title="Expiring within 7 days"
        tone="expiring"
        rows={filtered.expiringSoon}
        empty="Nobody is about to expire."
      />
      <Bucket
        title={`Expired in the last ${RECENTLY_EXPIRED_DAYS} days`}
        tone="expired"
        rows={filtered.recentlyExpired}
        empty="No recent lapses."
      />
      <Bucket
        title="Expired longer ago"
        tone="muted"
        rows={filtered.longExpired}
        empty="Nothing older."
        collapsedByDefault
      />
    </div>
  );
}

function Bucket({
  title,
  rows,
  tone,
  empty,
  collapsedByDefault = false,
}: {
  title: string;
  rows: SubscriptionRow[];
  tone: 'expiring' | 'expired' | 'muted';
  empty: string;
  collapsedByDefault?: boolean;
}) {
  // The long-expired list can be hundreds of people who left months ago.
  // Collapsed by default so it never buries the two lists that matter today.
  const [open, setOpen] = useState(!collapsedByDefault);

  const accent = {
    expiring: 'text-status-expiring border-status-expiring/30',
    expired: 'text-status-expired border-status-expired/30',
    muted: 'text-white/40 border-edge',
  }[tone];

  return (
    <section className="mb-8">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`mb-3 flex w-full items-center justify-between border-b-2 pb-2 text-left ${accent}`}
      >
        <h2 className={`text-xl ${accent.split(' ')[0]}`}>
          {title}
          <span className="tabular ml-3 text-base opacity-60">{rows.length}</span>
        </h2>
        <span className="text-sm opacity-50">{open ? '−' : '+'}</span>
      </button>

      {open &&
        (rows.length === 0 ? (
          <p className="py-4 text-sm text-white/25">{empty}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <RenewalRow key={row.subscription.id} row={row} />
            ))}
          </ul>
        ))}
    </section>
  );
}

function RenewalRow({ row }: { row: SubscriptionRow }) {
  const navigate = useNavigate();
  const { member, subscription } = row;

  return (
    <li className="card flex flex-wrap items-center gap-x-4 gap-y-3 p-4">
      <Link to={`/members/${member.id}`} className="min-w-[200px] flex-1 hover:text-brand">
        <div className="flex items-baseline gap-2">
          <span className="tabular text-sm font-bold text-brand">{member.sk_id}</span>
          <span className="text-lg font-semibold" dir="auto">
            {member.full_name}
          </span>
        </div>
        <div className="tabular text-sm text-white/40">{formatPhone(member.phone)}</div>
      </Link>

      <div className="min-w-[160px] flex-1">
        <div className="text-sm">{classLabel(subscription.class_key)}</div>
        <div className="text-sm text-white/40">
          {formatDisplayDate(subscription.end_date)}
        </div>
        <div
          className={`text-sm font-semibold ${
            row.daysRemaining >= 0 ? 'text-status-expiring' : 'text-status-expired'
          }`}
        >
          {describeRemaining(row.daysRemaining)}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <BalanceDueBadge remaining={row.remaining} size="sm" />
        {member.phone && <CopyPhoneButton phone={member.phone} />}
        <button
          onClick={() => navigate(`/renew/${subscription.id}`)}
          className="min-h-[40px] rounded-btn bg-brand px-5 text-xs font-bold uppercase
                     tracking-button text-paper transition-all duration-brand
                     hover:bg-brand-hover"
        >
          Renew
        </button>
      </div>
    </li>
  );
}
